import {
  refreshSteps,
  scriptLib,
  type CallData,
  type DrillData,
  type LibraryItemRow,
  type ScriptLib,
  type TagData,
} from '@coach/core';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useApp } from '../../lib/app';

export type CallRow = LibraryItemRow<CallData>;
export type TagRow = LibraryItemRow<TagData>;

/**
 * The team's saved play calls and tags, plus the few writes the script screens
 * need. Trashed rows are loaded too: a rep that used a deleted call still shows
 * the call's name.
 */
export function useScriptLib() {
  const app = useApp();
  const [rows, setRows] = useState<LibraryItemRow<unknown>[]>([]);
  const reload = useCallback(async () => {
    const [live, trash] = await Promise.all([app.repos.library.listLive(), app.repos.library.listTrash()]);
    setRows([...live, ...trash].filter((r) => r.kind === 'call' || r.kind === 'tag'));
  }, [app.repos.library]);
  useEffect(() => {
    void reload();
  }, [reload, app.syncStatus.lastSyncAt]);

  const lib: ScriptLib = useMemo(() => scriptLib(rows), [rows]);
  const calls = useMemo(
    () => (rows.filter((r) => r.kind === 'call' && !r.deleted_at) as CallRow[]).sort((a, b) => a.data.name.localeCompare(b.data.name, undefined, { numeric: true })),
    [rows],
  );
  const tags = useMemo(
    () => (rows.filter((r) => r.kind === 'tag' && !r.deleted_at) as TagRow[]).sort((a, b) => a.data.name.localeCompare(b.data.name, undefined, { numeric: true })),
    [rows],
  );
  const canEdit = useCallback((r: LibraryItemRow<unknown>) => app.isHead || r.owner_id === app.userId, [app.isHead, app.userId]);

  /** Add a call or tag, or return the live one already saved under that name. */
  const add = useCallback(
    async (kind: 'call' | 'tag', name: string, extra: Partial<CallData & TagData> = {}): Promise<string | null> => {
      const n = name.replace(/\s+/g, ' ').trim();
      if (!n) return null;
      const same = (kind === 'call' ? calls : tags).find((r) => r.data.name.toLowerCase() === n.toLowerCase());
      if (same) return same.id;
      const row = app.repos.library.blank({ kind, data: { id: '', name: n, ...extra }, sort_order: 0, version: 1 });
      (row.data as { id: string }).id = row.id;
      await app.repos.library.save(row);
      await reload();
      return row.id;
    },
    [app.repos.library, calls, tags, reload],
  );

  const update = useCallback(
    async (id: string, patch: Partial<CallData & TagData>) => {
      const row = rows.find((r) => r.id === id);
      if (!row || !canEdit(row)) return false;
      const data = { ...(row.data as object), ...patch } as Record<string, unknown>;
      for (const k of Object.keys(data)) if (data[k] === undefined) delete data[k];
      await app.repos.library.save({ ...row, data });
      await reload();
      return true;
    },
    [rows, canEdit, app.repos.library, reload],
  );

  /** Remember where the coach put each tag. Only rows this coach may edit. */
  const rememberPositions = useCallback(
    async (pos: Map<string, number>) => {
      let wrote = false;
      for (const [id, p] of pos) {
        const row = rows.find((r) => r.id === id && r.kind === 'tag' && !r.deleted_at) as TagRow | undefined;
        if (!row || row.data.pos === p || !canEdit(row)) continue;
        await app.repos.library.save({ ...row, data: { ...row.data, pos: p } });
        wrote = true;
      }
      if (wrote) await reload();
    },
    [rows, canEdit, app.repos.library, reload],
  );

  const trash = useCallback(
    async (id: string) => {
      await app.repos.library.trash(id);
      await reload();
    },
    [app.repos.library, reload],
  );

  /**
   * After a rename: rewrite the saved `steps` snapshot on every drill that uses
   * it (the reps already show the new name). Only drills this coach can edit;
   * anyone else's catch up the next time they are saved.
   */
  const refreshDrills = useCallback(
    async () => {
      const [live, gone] = await Promise.all([app.repos.library.listLive(), app.repos.library.listTrash()]);
      const next = scriptLib([...live, ...gone]);
      const drills = live.filter((r) => r.kind === 'drill') as LibraryItemRow<DrillData>[];
      for (const d of drills) {
        if (!canEdit(d)) continue;
        const fresh = refreshSteps(d.data, next);
        if (fresh) await app.repos.library.save({ ...d, data: fresh });
      }
    },
    [app.repos.library, canEdit],
  );

  return { rows, lib, calls, tags, reload, add, update, trash, rememberPositions, refreshDrills, canEdit };
}

export type ScriptLibApi = ReturnType<typeof useScriptLib>;
